import { useQuery } from "@tanstack/react-query";
import { portalApi, PortalInvite } from "@/lib/portalApi";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Eye, Lock } from "lucide-react";

/**
 * Per-person portal visibility picker (migration 085).
 * value = null/[] → everyone on the account; otherwise only the listed portal emails.
 * Renders nothing if the account has fewer than 2 active portal users (nothing to restrict).
 */
export function PortalVisibilityPicker({
  accountId,
  value,
  onChange,
}: {
  accountId: string;
  value: string[] | null | undefined;
  onChange: (emails: string[] | null) => void;
}) {
  const { data: invites = [] } = useQuery<PortalInvite[]>({
    queryKey: ["portal-invites", accountId],
    queryFn: () => portalApi.listInvites(accountId),
    staleTime: 60_000,
  });

  const activeEmails = [...new Set(invites.filter((i) => !i.revoked_at).map((i) => i.email.toLowerCase()))];
  if (activeEmails.length < 2) return null;

  const selected = (value ?? []).map((e) => e.toLowerCase());
  const restricted = selected.length > 0;

  function toggle(email: string) {
    // When unrestricted, everyone is effectively checked — unchecking one means "everyone but them"
    const base = restricted ? selected : activeEmails;
    const next = base.includes(email)
      ? base.filter((e) => e !== email)
      : [...base, email];
    onChange(next.length === 0 || next.length === activeEmails.length ? null : next);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          title={restricted ? `Portal visibility: ${selected.join(", ")}` : "Portal visibility: everyone on this account"}
          className={`flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border transition-colors ${
            restricted
              ? "border-violet-300 bg-violet-50 text-violet-700 hover:border-violet-400"
              : "border-slate-200 text-muted-foreground hover:border-slate-400 hover:bg-slate-100"
          }`}
        >
          {restricted ? <Lock className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          <span>{restricted ? `${selected.length} of ${activeEmails.length}` : "Everyone"}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel className="text-xs">Visible in portal to</DropdownMenuLabel>
        <DropdownMenuCheckboxItem
          checked={!restricted}
          onCheckedChange={() => onChange(null)}
          onSelect={(e) => e.preventDefault()}
          className="text-xs font-medium"
        >
          Everyone on this account
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {activeEmails.map((email) => (
          <DropdownMenuCheckboxItem
            key={email}
            checked={restricted ? selected.includes(email) : true}
            onCheckedChange={() => toggle(email)}
            onSelect={(e) => e.preventDefault()}
            className="text-xs"
          >
            {email}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
